import React, { useState, useMemo } from 'react';
import moment from 'moment';
import { getStatBadges, getBlockTypeLabel } from './exerciseUtils';
import {
  SegmentedRow,
  SegmentOption,
  SearchInput,
  ExerciseCard,
  CardTop,
  CardTopLeft,
  CardExerciseName,
  CardSubtitle,
  FavoriteButton,
  BadgeRow,
  Badge,
  BadgeValue,
  BadgeLabel,
  SectionLabel,
  EmptyState,
  LoadingContainer,
} from './components';

const StatBadges = ({ record, blockType, label }) => {
  const badges = getStatBadges(record, blockType);
  if (!badges.length) return null;
  return (
    <div style={{ marginBottom: '0.8rem' }}>
      {label && <SectionLabel>{label}</SectionLabel>}
      <BadgeRow>
        {badges.map((b, i) => (
          <Badge key={i}>
            <BadgeValue>{b.value}</BadgeValue>
            <BadgeLabel>{b.label}</BadgeLabel>
          </Badge>
        ))}
      </BadgeRow>
    </div>
  );
};

const ExerciseHistoryList = ({
  exercises,
  favoriteExercises,
  loading,
  onSelectExercise,
  onToggleFavorite,
}) => {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');

  const favCount = useMemo(
    () => exercises.filter((ex) => favoriteExercises.includes(ex.exerciseUid)).length,
    [exercises, favoriteExercises],
  );

  const filtered = useMemo(() => {
    let list = exercises;
    if (tab === 'favorites') {
      list = list.filter((ex) => favoriteExercises.includes(ex.exerciseUid));
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((ex) => ex.exerciseName.toLowerCase().includes(q));
    }
    return list;
  }, [exercises, favoriteExercises, tab, search]);

  if (loading) {
    return <LoadingContainer>Loading exercise history...</LoadingContainer>;
  }

  return (
    <div>
      <SegmentedRow>
        <SegmentOption active={tab === 'all'} onClick={() => setTab('all')}>
          All ({exercises.length})
        </SegmentOption>
        <SegmentOption active={tab === 'favorites'} onClick={() => setTab('favorites')}>
          Favorites ({favCount})
        </SegmentOption>
      </SegmentedRow>

      <SearchInput
        placeholder="Search exercises..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {!filtered.length ? (
        <EmptyState>
          {!exercises.length
            ? 'No exercise history yet'
            : tab === 'favorites'
            ? 'No favorite exercises'
            : 'No exercises match your search'}
        </EmptyState>
      ) : (
        filtered.map((ex) => (
          <ExerciseCard key={ex.exerciseUid} onClick={() => onSelectExercise(ex)}>
            <CardTop>
              <CardTopLeft>
                <CardExerciseName>{ex.exerciseName}</CardExerciseName>
                <CardSubtitle>
                  {getBlockTypeLabel(ex.blockType)}
                  {ex.lastWorkoutDate &&
                    ` \u00B7 Last: ${moment(ex.lastWorkoutDate).format('DD/MM/YYYY')}`}
                </CardSubtitle>
              </CardTopLeft>
              <FavoriteButton
                active={favoriteExercises.includes(ex.exerciseUid)}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(ex.exerciseUid);
                }}
              >
                ★
              </FavoriteButton>
            </CardTop>
            <div style={{ display: 'flex', gap: '1.6rem' }}>
              <div style={{ flex: 1 }}>
                <StatBadges record={ex.lastRecord} blockType={ex.blockType} label="Last Record" />
              </div>
              <div style={{ flex: 1 }}>
                <StatBadges record={ex.maxRecord} blockType={ex.blockType} label="Max Record" />
              </div>
            </div>
          </ExerciseCard>
        ))
      )}
    </div>
  );
};

export default ExerciseHistoryList;
